import React, { useEffect, useState, useRef, useCallback } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { RegisterResponseDTO } from '@/core/api/dtos/response/auth/register'
import { EmailAvailabilityResponseDTO } from '@/core/api/dtos/response/users/emailAvailability'
import { utmService, UTMData } from '@/core/api/services/utmService'
import { useBusinessCategories } from '@/core/hooks/misc/useBusinessCategories'
import { useDemographicAgeGroups } from '@/core/hooks/misc/useDemographicAgeGroups'
// import { useDemographicGenders } from '@/core/hooks/misc/useDemographicGenders'
import { useDemographicSocioeconomicGroups } from '@/core/hooks/misc/useDemographicSocioeconomicGroups'
import { useAuth } from '@/core/hooks/useAuth'
import { useMetaPixel } from '@/core/hooks/useMetaPixel'
import { useUserEmailAvailable } from '@/core/hooks/useUser'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { RegisterFlowProvider, useRegisterFlow } from './RegisterFlowContext'
import { RegisterUI } from './RegisterUI'

type RegisterUIWithFlowProps = {
	form: any
	isEmailAvailable: boolean | null
	isCheckingEmailAvailability?: boolean
	onFinish: (values: RegisterRequestDTO) => void
	isLoading: boolean
	error: string | null
	businessCategories: any[]
	demographicAgeGroups: any[]
	demographicSocioeconomicGroups: any[]
	registrationComplete?: boolean
	onNavigateAfterLoading?: () => void
}

const RegisterUIWithFlow: React.FC<RegisterUIWithFlowProps> = ({
	form,
	isEmailAvailable,
	isCheckingEmailAvailability,
	onFinish,
	isLoading,
	error,
	businessCategories,
	demographicAgeGroups,
	demographicSocioeconomicGroups,
	registrationComplete,
	onNavigateAfterLoading,
}) => {
	const { currentStep } = useRegisterFlow()

	/**
	 * Navigation blocking to prevent losing progress between steps 1–6.
	 * - Warns on browser/tab close or full page navigation via beforeunload.
	 * - Exposed to UI so it can optionally confirm before leaving via links (e.g. logo).
	 */
	const isInBlockingStep =
		Number.isFinite(currentStep) && currentStep >= 1 && currentStep <= 7

	const isFormDirty = form.formState.isDirty
	const shouldBlockNavigation = isInBlockingStep && isFormDirty

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!shouldBlockNavigation) return

			event.preventDefault()
			event.returnValue = ''
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload)
		}
	}, [shouldBlockNavigation])

	return (
		<RegisterUI
			form={form}
			isEmailAvailable={isEmailAvailable}
			isCheckingEmailAvailability={isCheckingEmailAvailability}
			onFinish={onFinish}
			isLoading={isLoading}
			error={error}
			businessCategories={businessCategories}
			demographicAgeGroups={demographicAgeGroups}
			// demographicGenders={demographicGenders}
			demographicSocioeconomicGroups={demographicSocioeconomicGroups}
			shouldBlockExit={shouldBlockNavigation}
			registrationComplete={registrationComplete}
			onNavigateAfterLoading={onNavigateAfterLoading}
		/>
	)
}

export const Register: React.FC = () => {
	const { t } = useTranslation('auth')
	const navigate = useNavigate()
	const validationSchema = yup.object().shape({
		email: yup
			.string()
			.email(t('register.errors.invalidEmail'))
			.required(
				t('register.errors.required', {
					field: t('register.email').toLowerCase(),
				})
			),
		password: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.password').toLowerCase(),
				})
			)
			.min(8, t('register.errors.passwordMinLength')),
		password_confirmation: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.confirmPassword').toLowerCase(),
				})
			)
			.min(8, t('register.errors.passwordMinLength'))
			.oneOf([yup.ref('password')], t('register.errors.passwordMismatch')),
		firstname: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.firstname').toLowerCase(),
				})
			)
			.max(
				40,
				t('register.errors.maxLength', {
					field: t('register.firstname').toLowerCase(),
					max: 40,
				})
			),
		lastname: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.lastname').toLowerCase(),
				})
			)
			.max(
				80,
				t('register.errors.maxLength', {
					field: t('register.lastname').toLowerCase(),
					max: 80,
				})
			),
		phone_number: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.phoneNumber').toLowerCase(),
				})
			)
			.max(20, t('register.errors.phoneNumberMaxLength', { max: 20 })),
		country: yup.string().required(
			t('register.errors.required', {
				field: t('register.country').toLowerCase(),
			})
		),
		parent_business_category_id: yup.number().required(
			t('register.errors.required', {
				field: t('register.businessSubcategory').toLowerCase(),
			})
		),
		business_category_id: yup.number().required(
			t('register.errors.required', {
				field: t('register.businessCategory').toLowerCase(),
			})
		),
		business_category_description: yup
			.string()
			.optional()
			.max(100, t('registerStep4.errors.businessCategoryDescriptionMaxLength')),
		plan_key: yup.string().required(
			t('register.errors.required', {
				field: t('register.planKey').toLowerCase(),
			})
		),
		period_cycle: yup.string().required(
			t('register.errors.required', {
				field: t('register.periodCycle').toLowerCase(),
			})
		),
		brand_name: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.brandName').toLowerCase(),
				})
			)
			.min(
				2,
				t('register.errors.minLength', {
					field: t('register.brandName').toLowerCase(),
					min: 2,
				})
			)
			.max(
				100,
				t('register.errors.maxLength', {
					field: t('register.brandName').toLowerCase(),
					max: 100,
				})
			),
		total_company_stores: yup
			.number()
			.transform((value, originalValue) =>
				originalValue === '' || originalValue === undefined ? undefined : value
			)
			.nullable()
			.required(
				t('register.errors.required', {
					field: t('register.totalCompanyStores').toLowerCase(),
				})
			)
			.min(
				1,
				t('register.errors.minLength', {
					field: t('register.totalCompanyStores').toLowerCase(),
					min: 1,
				})
			)
			.max(
				9999,
				t('register.errors.maxLength', {
					field: t('register.totalCompanyStores').toLowerCase(),
					max: 9999,
				})
			),
		terms_accepted: yup.boolean().required(
			t('register.errors.required', {
				field: t('register.termsAndConditionsField').toLowerCase(),
			})
		),
		brand_identities: yup
			.array()
			.of(yup.number())
			.min(1, 'At least one brand identity must be selected')
			.required('Brand identities are required'),
		// cultural_identity_id: yup
		// 	.number()
		// 	.required('Cultural identity is required'),
		demographic_ages: yup
			.array()
			.of(yup.string())
			.min(1, 'At least one age range must be selected')
			.required('Target age ranges are required'),
		// demographic_gender: yup.string().required('Target gender is required'),
		demographic_socioeconomics: yup
			.array()
			.of(yup.string())
			.min(1, 'At least one socioeconomic level must be selected')
			.required('Target socioeconomic levels are required'),
	})

	const [params] = useSearchParams()

	// Get UTMs from URL or fallback to localStorage
	const storedUTMs = utmService.get()
	const utmValues: UTMData = {
		utm_id: params.get('utm_id') || storedUTMs.utm_id || undefined,
		utm_source: params.get('utm_source') || storedUTMs.utm_source || undefined,
		utm_medium: params.get('utm_medium') || storedUTMs.utm_medium || undefined,
		utm_campaign:
			params.get('utm_campaign') || storedUTMs.utm_campaign || undefined,
		utm_content:
			params.get('utm_content') || storedUTMs.utm_content || undefined,
		referral_id:
			params.get('referral_id') || storedUTMs.referral_id || undefined,
	}

	// Persist UTMs to localStorage (URL params take priority, merge with stored)
	useEffect(() => {
		const urlUTMs: UTMData = {
			utm_id: params.get('utm_id') || undefined,
			utm_source: params.get('utm_source') || undefined,
			utm_medium: params.get('utm_medium') || undefined,
			utm_campaign: params.get('utm_campaign') || undefined,
			utm_content: params.get('utm_content') || undefined,
			referral_id: params.get('referral_id') || undefined,
		}
		utmService.merge(urlUTMs)
	}, [params])

	const form = useForm<RegisterRequestDTO, any, RegisterRequestDTO>({
		defaultValues: {
			firstname: params.get('firstname') || '',
			lastname: params.get('lastname') || '',
			email: params.get('email') || '',
			phone_number: params.get('phone_number') || '',
			country: (params.get('country') as any) || undefined,
			parent_business_category_id: params.get('parent_business_category_id')
				? parseInt(params.get('parent_business_category_id') || '')
				: params.get('main_category')
					? undefined
					: undefined,
			business_category_id: params.get('business_category_id')
				? parseInt(params.get('business_category_id') || '')
				: undefined,
			brand_name: params.get('brand_name') || '',
			total_company_stores: params.get('total_company_stores')
				? parseInt(params.get('total_company_stores') || '')
				: 1,
			plan_key: 'BT_LITE_PREMIUM',
			period_cycle: 'monthly_1',
			brand_identities: [],
			business_category_description: undefined,
			// cultural_identity_id: undefined,
			demographic_ages: [],
			// demographic_gender: '',
			demographic_socioeconomics: [],
			...utmValues,
		},
		resolver: yupResolver(validationSchema) as any,
		mode: 'onChange',
		reValidateMode: 'onChange',
	})
	const [error, setError] = useState<string | null>(null)
	const { useRegister, fetchUserAndLogin } = useAuth()
	const { trackEvent: trackPixelEvent } = useMetaPixel()
	const [isEmailAvailable, setIsEmailAvailable] = useState<boolean | null>(null)

	const currentEmail = form.watch('email') || ''
	const debouncedEmail = useDebounce(currentEmail, 500)

	const previousEmailRef = useRef<string>('')

	const {
		data: businessCategories = [],
		isLoading: isLoadingBusinessCategories,
	} = useBusinessCategories()

	const {
		data: demographicAgeGroups = [],
		isLoading: isLoadingDemographicAgeGroups,
	} = useDemographicAgeGroups()

	// const {
	// 	data: demographicGenders = [],
	// 	isLoading: isLoadingDemographicGenders,
	// } = useDemographicGenders()

	const {
		data: demographicSocioeconomicGroups = [],
		isLoading: isLoadingDemographicSocioeconomicGroups,
	} = useDemographicSocioeconomicGroups()

	useEffect(() => {
		const mainCategoryParam = params.get('main_category')
		if (
			mainCategoryParam &&
			businessCategories.length > 0 &&
			!form.getValues('parent_business_category_id')
		) {
			const mainCategory = businessCategories.find(
				(cat) =>
					!cat.parentId &&
					(cat.name.toLowerCase().replace(/\s+/g, '_') ===
						mainCategoryParam.toLowerCase() ||
						cat.name.toLowerCase() ===
							mainCategoryParam.toLowerCase().replace(/_/g, ' '))
			)
			if (mainCategory) {
				form.setValue('parent_business_category_id', mainCategory.id)
			}
		}
	}, [businessCategories, params, form])

	const {
		mutate: checkEmailAvailability,
		isPending: isCheckingEmailAvailability,
	} = useUserEmailAvailable({
		options: {
			onSuccess: (data: EmailAvailabilityResponseDTO) => {
				setIsEmailAvailable(data.available)
			},
			onError: (err: any) => {
				setError(extractErrorMessage(err))
			},
		},
	})

	// Track if registration completed successfully (for loading simulation coordination)
	const [registrationComplete, setRegistrationComplete] = useState(false)

	// Track when the mutation starts for minimum wait enforcement
	const mutationStartTimeRef = useRef<number | null>(null)

	const { mutate, isPending } = useRegister({
		options: {
			onSuccess: async (data: RegisterResponseDTO) => {
				setError(null)

				// Clear persisted UTMs after successful registration
				utmService.clear()

				// Track Meta Pixel CompleteRegistration event
				trackPixelEvent('CompleteRegistration')

				// Enforce minimum 10-second wait from mutation start before allowing completion
				if (mutationStartTimeRef.current) {
					const elapsed = Date.now() - mutationStartTimeRef.current
					const MIN_WAIT_TIME = 10000 // 10 seconds
					const remainingWait = Math.max(0, MIN_WAIT_TIME - elapsed)

					if (remainingWait > 0) {
						await new Promise((resolve) => setTimeout(resolve, remainingWait))
					}
				}

				// Log the user in (store token in auth state)
				try {
					await fetchUserAndLogin(data.access_token)
				} catch (_e) {
					// If fetching the user fails, surface error and stop the flow
					setError('Failed to initialize session after registration.')
					return
				}

				setRegistrationComplete(true)
				form.reset()
			},
			onError: (err: any) => {
				setError(extractErrorMessage(err))
			},
		},
	})

	// Callback for UI to navigate after loading simulation completes
	const handleNavigateAfterLoading = useCallback(() => {
		navigate('/zones', { replace: true })
	}, [navigate])

	useEffect(() => {
		const checkEmail = async () => {
			if (!debouncedEmail || debouncedEmail === previousEmailRef.current) {
				return
			}

			const isValid = await form.trigger(['email'])

			if (isValid && !isCheckingEmailAvailability) {
				previousEmailRef.current = debouncedEmail
				checkEmailAvailability({ email: debouncedEmail })
			}
		}

		checkEmail()
	}, [
		debouncedEmail,
		form,
		checkEmailAvailability,
		isCheckingEmailAvailability,
	])

	const handleSubmit = (values: RegisterRequestDTO) => {
		setError(null)
		// Record the start time for minimum wait enforcement
		mutationStartTimeRef.current = Date.now()
		mutate({ ...values, auto_setup: true })
	}

	return (
		<RegisterFlowProvider form={form}>
			<RegisterUIWithFlow
				form={form}
				isEmailAvailable={isEmailAvailable}
				isCheckingEmailAvailability={isCheckingEmailAvailability}
				onFinish={handleSubmit}
				isLoading={
					isPending ||
					isLoadingBusinessCategories ||
					isLoadingDemographicAgeGroups ||
					// isLoadingDemographicGenders ||
					isLoadingDemographicSocioeconomicGroups
				}
				error={error}
				businessCategories={businessCategories}
				demographicAgeGroups={demographicAgeGroups}
				demographicSocioeconomicGroups={demographicSocioeconomicGroups}
				registrationComplete={registrationComplete}
				onNavigateAfterLoading={handleNavigateAfterLoading}
			/>
		</RegisterFlowProvider>
	)
}
